// FIRForm.js
import * as Yup from 'yup'

export const FIR_INITIAL_VALUES = {
  firNumber: '',
  firDate: '',
  policeStation: '',
  otherPoliceStation: '',
  section: [],
  otherSection: '',
  allegations: '',
  remarks: '',
  status: '',

  // Untraced
  untracedOutcome: '',
  untracedCctnsDate: '',

  // Cancellation
  cancellationOutcome: '',
  cancellationCctnsDate: '',

  // Challan
  challanSubmitted: '',
  challanDate: '',
  challanCctnsDate: '',
  chargesFramed: '',
  chargesDate: '',
  trialStatus: '',

  // Judgment
  judgmentDate: '',
  judgmentOutcome: '',
  judgmentAgainstAppeal: '',
  judgmentDetailsOfAppeal: '',

  // Sentence
  sentenceType: '',
  imprisonmentDurationYears: '',
  imprisonmentDurationMonths: '',
  imprisonmentDurationDays: '',
  bothSentenceMonths: '',
  bothSentenceYears: '',
  bothSentenceDays: '',
  bothSentenceFine: '',

  fineAmount: '',
}

export const FIR_VALIDATION_SCHEMA = Yup.object({
  firNumber: Yup.string().required('FIR Number is required'),
  firDate: Yup.date().required('FIR Date is required'),
  policeStation: Yup.string().required('Police Station is required'),
  section: Yup.array().min(1, "At least one section is required"),
  status: Yup.string().required('Status is required'),
  allegations: Yup.string().required('Allegations is required'),


  // Conditional validations
  untracedOutcome: Yup.string().when('status', {
    is: 'untraced',
    then: (schema) => schema.required('Outcome is required'),
  }),

  otherSection: Yup.string().when("section", {
    is: (section) => section?.includes("other"),
    then: (schema) => schema.required("Other Section is required"),
  }),

  otherPoliceStation: Yup.string().when("policeStation", {
    is: (section) => section?.includes("other"),
    then: (schema) => schema.required("Other Police Station is required"),
  }),
  untracedCctnsDate: Yup.date().when('status', {
    is: 'untraced',
    then: (schema) => schema.required('CCTNS Date is required'),
  }),
  cancellationOutcome: Yup.string().when('status', {
    is: 'cancellation',
    then: (schema) => schema.required('Outcome is required'),
  }),
  cancellationCctnsDate: Yup.date().when('status', {
    is: 'cancellation',
    then: (schema) => schema.required('CCTNS Date is required'),
  }),

  // Challan Submission
  challanSubmitted: Yup.string().when('status', {
    is: 'challan',
    then: (schema) => schema.required('Challan Submission is required'),
  }),

  challanDate: Yup.date().when('challanSubmitted', {
    is: 'yes',
    then: (schema) => schema.required('Challan Date is required'),
  }),

  challanCctnsDate: Yup.date().when('challanSubmitted', {
    is: 'yes',
    then: (schema) => schema.required('CCTNS Date is required'),
  }),

  chargesFramed: Yup.string().when('challanSubmitted', {
    is: 'yes',
    then: (schema) => schema.required('Charges Framed is required'),
  }),
  chargesDate: Yup.date().when('chargesFramed', {
    is: 'yes',
    then: (schema) => schema.required('Charges Date is required'),
  }),
  trialStatus: Yup.string().when('chargesFramed', {
    is: 'yes',
    then: (schema) => schema.required('Trial Status is required'),
  }),

  judgmentDate: Yup.date().when('trialStatus', {
    is: 'judgment',
    then: (schema) => schema.required('Judgment Date is required'),
  }),
  judgmentOutcome: Yup.string().when('trialStatus', {
    is: 'judgment',
    then: (schema) => schema.required('Judgment Outcome is required'),
  }),
  imprisonmentDurationMonths: Yup.string().when('sentenceType', {
    is: 'imprisonment',
    then: (schema) => schema.required('Months is required'),
  }),
  imprisonmentDurationYears: Yup.string().when('sentenceType', {
    is: 'imprisonment',
    then: (schema) => schema.required('Years is required'),
  }),
  imprisonmentDurationDays: Yup.string().when('sentenceType', {
    is: 'imprisonment',
    then: (schema) => schema.required('Days is required'),
  }),

  fineAmount: Yup.number().when('sentenceType', {
    is: 'fine',
    then: (schema) =>
      schema
        .typeError('Fine must be a number')
        .min(1, 'Fine cannot be negative')
        .required('Fine is required'),
  }),

  bothSentenceMonths: Yup.string().when('sentenceType', {
    is: 'bothImprisonmentAndFine',
    then: (schema) => schema.required('Months is required'),
  }),
  bothSentenceYears: Yup.string().when('sentenceType', {
    is: 'bothImprisonmentAndFine',
    then: (schema) => schema.required('Years is required'),
  }),
  bothSentenceDays: Yup.string().when('sentenceType', {
    is: 'bothImprisonmentAndFine',
    then: (schema) => schema.required('Days is required'),
  }),

  bothSentenceFine: Yup.string().when('sentenceType', {
    is: 'bothImprisonmentAndFine',
    then: (schema) =>
      schema
        .typeError('Fine must be a number')
        .min(1, 'Fine cannot be negative')
        .required('Fine is required'),
  }),
})

const defaultPunishment = {
  PermanentEffect: 'Stoppage of increment with Permanent Effect',
  TemporaryEffect: 'Stoppage of increment with Temporary Effect',
}

export const DE_INITIAL_VALUES = {
  deInitiationDate: '',
  deUnit: '',
  deSubUnit: '',
  deAllegations: '',
  deStatus: '',
  deResultDe: '',
  dePunishment: '',
  dePunishmentTemp: '',
  dePunishmentType: '',
  deAppealOutcome: '',
  deRevisionAppealOutcome: '',
  annualStoppage: '',
  dePunishmentSecondary: '',
  dePunishmentSecondaryType: '',
  annualStoppageSecondary: '',
  dePunishmentThird: '',
  annualStoppageThird: '',
  deRemark: '',
  deAnyPetiton: '',
  deCourtCase: ''
}

export const DE_VALIDATION_SCHEMA = Yup.object({
  deInitiationDate: Yup.date().required('Initiation Date is required'),
  deUnit: Yup.string().required('Unit is required'),
  // deSubUnit: Yup.string().required('Sub unit is required'),
  deAllegations: Yup.string().required('Allegations is required'),
  deStatus: Yup.string().required('Status is required'),
  // dePunishmentType: Yup.string().when('deStatus', {
  //   is: 'decided',
  //   then: (schema) => schema.required('Punishment Type is required'),
  // }),
  dePunishment: Yup.string().when('deResultDe', {
    is: 'Punishment Imposed',
    then: (schema) => schema.required('Punishment is required'),
  }),
  deResultDe: Yup.string().when('deStatus', {
    is: 'decided',
    then: (schema) => schema.required('Result of DE is required'),
  }),

  // deAppealOutcome: Yup.string().when('deStatus', {
  //   is: 'decided',
  //   then: (schema) => schema.required('Appeal Outcome is required'),
  // }),
  // deRevisionAppealOutcome: Yup.string().when('deStatus', {
  //   is: 'decided',
  //   then: (schema) => schema.required('Revision Appeal Outcome is required'),
  // }),
  annualStoppage: Yup.number()
    .typeError('Only numbers are allowed')
    .when('dePunishment', {
      is: (val) =>
        val === defaultPunishment.PermanentEffect ||
        val === defaultPunishment.TemporaryEffect,
      then: (schema) =>
        schema
          .integer('Must be a whole number')
          .min(1, 'Value must be greater than 0'),
    }),


  dePunishmentSecondary: Yup.string().when('deAppealOutcome', {
    is: (val) => val === 'partially' || val === 'punishmentEnhanced',
    then: (schema) => schema.required('Punishment is required'),
  }),

  dePunishmentThird: Yup.string().when('deRevisionAppealOutcome', {
    is: (val) => val === 'partially' || val === 'punishmentEnhanced',
    then: (schema) => schema.required('Punishment is required'),
  }),

  // dePunishmentSecondaryType: Yup.string().when('deAppealOutcome', {
  //   is: (val) => val === 'partially',
  //   then: (schema) => schema.required('Punishment Type is required'),
  // }),

  annualStoppageSecondary: Yup.string().when('dePunishmentSecondary', {
    is: (val) =>
      val === defaultPunishment.PermanentEffect ||
      val === defaultPunishment.TemporaryEffect,
    then: (schema) => schema.required('Annual Stoppage is required'),
  }),


  annualStoppageThird: Yup.string().when('dePunishmentThird', {
    is: (val) =>
      val === defaultPunishment.PermanentEffect ||
      val === defaultPunishment.TemporaryEffect,
    then: (schema) => schema.required('Annual Stoppage is required'),
  }),
})

export const ACR_INITIAL_VALUES = {
  assessmentFrom: '',
  assessmentTo: '',
  integrityDoubtful: '',
  reliabilityDoubtful: '',
  representationFiled: '',
  representationStatus: '',
  decisionDate: '',
  decisionOutcome: '',
  remarks: '',
  representationFiledSecondary: '',
  representationStatusSecondary: '',
  decisionDateSecondary: '',
  decisionOutcomeSecondary: '',
  remarksSecondary: '',
}

export const ACR_VALIDATION_SCHEMA = Yup.object({
  assessmentFrom: Yup.date().required('Assessment From Date is required'),
  assessmentTo: Yup.date().required('Assessment To Date is required'),
  integrityDoubtful: Yup.string().required('Integrity Doubtful is required'),
  reliabilityDoubtful: Yup.string().required(
    'Reliability Doubtful is required'
  ),
  representationFiled: Yup.string().required(
    'Representation Filed is required'
  ),
  representationStatus: Yup.string().when('representationFiled', {
    is: 'yes',
    then: (schema) => schema.required('Status is required'),
  }),

  decisionDate: Yup.date().when('representationStatus', {
    is: 'decided',
    then: (schema) => schema.required('Decision Date is required'),
  }),
  decisionOutcome: Yup.string().when('representationStatus', {
    is: 'decided',
    then: (schema) => schema.required('Outcome is required'),
  }),


  representationFiledSecondary: Yup.string().required(
    'Representation Filed is required'
  ),

  representationStatusSecondary: Yup.string().when(
    'representationFiledSecondary',
    {
      is: 'yes',
      then: (schema) => schema.required('Status is required'),
    }
  ),

  decisionDateSecondary: Yup.date().when('representationStatusSecondary', {
    is: 'decided',
    then: (schema) => schema.required('Decision Date is required'),
  }),
  decisionOutcomeSecondary: Yup.string().when('representationStatusSecondary', {
    is: 'decided',
    then: (schema) => schema.required('Outcome is required'),
  }),

})
